import React, { MouseEventHandler } from 'react'

import { styled, css } from '../../../styled'

export type Props = {
  bgColor?: 'cta' | 'primary' | 'transparent'
  block?: boolean
  className?: string
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  size?: 'normal' | 'small' | 'large'
  type?: 'button' | 'submit'
}

/* styles */
const backgroundStyles = css<Props>`
  ${({ bgColor = 'primary', theme }) => {
    switch (bgColor) {
      case 'transparent':
        return `
        color: ${theme.colorPallets.primary.normal};
        background-color: transparent;

        &:hover {
          color: ${theme.colorPallets.primary.hover};
        }

        &:disabled {
          color: ${theme.colorPallets.primary.disable};
        }
      `
      default:
        return `
      color: ${theme.colors.white};
      background-color: ${theme.colorPallets[bgColor].normal};

      &:hover {
        background-color: ${theme.colorPallets[bgColor].hover};
      }

      &:disabled {
        background-color: ${theme.colorPallets[bgColor].disable};
      }
    `
    }
  }}
`

const disabledStyles = css<Props>`
  ${({ disabled }) =>
    disabled ? ` pointer-events: none;` : `cursor: pointer;`}
`

const displayStyles = css<Props>`
  ${({ block }) =>
    block
      ? `
  display: flex;
  width: 100%;
`
      : `
  display: inline-flex;
`}
`

const sizeStyles = css<Props>`
  ${({ size, theme }) => {
    switch (size) {
      case 'large':
        return `
        font-size: ${theme.sizes.font.s16};
        `
      case 'small':
        return `
        font-size: ${theme.sizes.font.s12};
        `
      default:
        return `
        font-size: ${theme.sizes.font.s14};
        `
    }
  }}
`

const styles = css<Props>`
  align-items: center;
  justify-content: center;
  padding: 0.85em;
  text-decoration: none;
  white-space: nowrap;
  border: none;
  border-radius: ${props => props.theme.sizes.borderRadius};
  transition: background-color 0.3s ease;
  appearance: none;

  ${backgroundStyles}
  ${disabledStyles}
  ${displayStyles}
  ${sizeStyles}

  &:focus {
    outline: none;
  }
`

const BaseButton: React.FC<Props> = ({
  children,
  className,
  disabled,
  onClick,
  type,
}) => (
  <button
    className={className}
    disabled={disabled}
    onClick={onClick}
    type={type}
  >
    {children}
  </button>
)

export const Button = styled(BaseButton)`
  ${styles}
`
